import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../hooks/useAPI';
import { getTalentById } from '../services/api/talents';
import Button from '../components/ui/Button';
import { getFullAttachmentUrl } from '../services/api/index.js';

const TalentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 从API获取人才详情
  const { 
    data: apiTalent, 
    loading: apiLoading, 
    error: apiError,
    refetch: refetchTalent 
  } = useAPI(() => getTalentById(id), [id]);

  useEffect(() => {
    // 从sessionStorage中读取人才信息
    const loadTalentFromSession = () => {
      try {
        setLoading(true);
        console.log('当前路由ID:', id);
        
        // 尝试从selectedTalent中读取
        const selectedTalent = sessionStorage.getItem('selectedTalent');
        console.log('selectedTalent存储内容:', selectedTalent);
        
        if (selectedTalent) {
          const talentData = JSON.parse(selectedTalent);
          console.log('解析后的selectedTalent:', talentData);
          console.log('selectedTalent的talentId:', talentData.talentId);
          
          // 验证读取的数据是否与当前路由的id匹配
          if (talentData.talentId === id) {
            console.log('selectedTalent与路由ID匹配，设置人才信息');
            setTalent(talentData);
            setError(null);
            return;
          } else {
            console.log('selectedTalent与路由ID不匹配');
          }
        } else {
          console.log('sessionStorage中没有selectedTalent');
        }
        
        // 如果selectedTalent不存在或不匹配，尝试从allTalents中查找
        const allTalents = sessionStorage.getItem('allTalents');
        console.log('allTalents存储内容:', allTalents);
        
        if (allTalents) {
          const talentsArray = JSON.parse(allTalents);
          console.log('解析后的allTalents数组长度:', talentsArray.length);
          
          const foundTalent = talentsArray.find(t => t.talentId === id);
          console.log('从allTalents中找到的人才:', foundTalent);
          
          if (foundTalent) {
            console.log('从allTalents中找到人才，设置人才信息');
            setTalent(foundTalent);
            setError(null);
            return;
          } else {
            console.log('从allTalents中没有找到匹配的人才');
          }
        } else {
          console.log('sessionStorage中没有allTalents');
        }
        
        // 如果都没有找到，尝试使用API返回的数据
        console.log('尝试使用API返回的人才数据:', apiTalent);
        if (apiTalent) {
          console.log('从API获取到人才信息，设置人才信息');
          setTalent(apiTalent);
          setError(null);
          return;
        }
        
        // 如果都没有找到，显示错误
        console.log('所有尝试都失败，显示错误');
        setError('未找到人才信息');
        setTalent(null);
      } catch (err) {
        console.error('读取人才信息失败:', err);
        setError('读取人才信息失败');
        setTalent(null);
      } finally {
        setLoading(false);
      }
    };

    loadTalentFromSession();
  }, [id, apiTalent]);

  // 处理返回
  const handleBack = () => {
    navigate('/talents');
  };

  // 加载状态
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error || !talent) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center py-16">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <p className="text-gray-600 mb-6">{error || '人才信息不存在'}</p>
          <Button onClick={handleBack} className="bg-blue-600 hover:bg-blue-700 text-white">
            返回人才列表
          </Button>
        </div>
      </div>
    );
  }

  // 从data字段中获取人才详细信息
  const talentData = talent.data || {};

  return (
    <div className="min-h-screen bg-gray-50">


      {/* 主要内容 */}
      <div className="container mx-auto px-4 py-8">
        {/* 返回按钮 */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={handleBack}
            className="text-gray-600 border-gray-300 hover:bg-gray-100"
          >
            ← {t('talents.detail.backToList')}
          </Button>
        </div>

        {/* 人才详情卡片 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          {/* 基本信息 */}
          <div className="p-8">
            <div className="flex flex-col md:flex-row md:items-center mb-8">
              {/* 头像 */}
              <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-8">
                <img
                  src={getFullAttachmentUrl(talent.imageId) || '/images/default-avatar.svg'}
                  alt={talentData.name || '人才头像'}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-100"
                  onError={(e) => {
                    e.target.src = '/images/default-avatar.svg';
                  }}
                />
              </div>

              {/* 个人信息 */}
              <div className="flex-grow">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {talentData.name || '未知'}
                </h2>
                <p className="text-gray-600 mb-4">
                  {talentData.occupation || '未指定职业'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('talents.detail.country')}</p>
                    <p className="text-gray-700">{talentData.country || '未指定'}</p>
                  </div>
                  {talentData.certification && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{t('talents.detail.certification')}</p>
                      <p className="text-gray-700">{talentData.certification}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 详细信息 */}
            <div className="border-t border-gray-200 pt-8">
              {talentData.summary && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('talents.detail.summary')}</h3>
                  <p className="text-gray-600 leading-relaxed">{talentData.summary}</p>
                </div>
              )}

              {talentData.skill && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('talents.detail.skills')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {talentData.skill.split('；').map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {talentData.profession && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('talents.detail.profession')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {talentData.profession.split(',').map((profession, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {profession}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {talentData.achievement && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('talents.detail.achievements')}</h3>
                  <p className="text-gray-600 leading-relaxed">{talentData.achievement}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentDetail;